/**
 * Jalali (Persian) ↔ Gregorian calendar conversion.
 *
 * The backend stores and sends calendar dates as Gregorian `YYYY-MM-DD`
 * only — Jalali is presentation (backend-nest ADR-0008, docs/api-contract.md
 * §2) — while the profile form collects a Jalali birthday, the calendar a
 * learner knows their birthday in. This is the crossing, and it mirrors
 * `core/format/JalaliDate.kt` in the Android app line for line: the same
 * jalaali algorithm (Behrooz Kazemi et al.), exact for Jalali years 1–3177.
 * No `Intl` calendar: the integer routine is easier to prove against a
 * handful of known dates than a locale table, and it cannot differ by
 * browser.
 *
 * Months are 1-based on both sides (فروردین = 1).
 */

const BREAKS = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178]

const div = (a, b) => Math.trunc(a / b)
const mod = (a, b) => a - div(a, b) * b

function calendar(jy) {
  const breakCount = BREAKS.length
  const gy = jy + 621
  let leapJ = -14
  let jp = BREAKS[0]
  if (jy < jp || jy >= BREAKS[breakCount - 1]) throw new RangeError(`Jalali year out of range: ${jy}`)

  let jump = 0
  for (let i = 1; i < breakCount; i += 1) {
    const jm = BREAKS[i]
    jump = jm - jp
    if (jy < jm) break
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4)
    jp = jm
  }
  let n = jy - jp

  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4)
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150
  const march = 20 + leapJ - leapG

  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33
  let leap = mod(mod(n + 1, 33) - 1, 4)
  if (leap === -1) leap = 4
  return { leap, gy, march }
}

function gregorianToJulianDay(gy, gm, gd) {
  let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752
  return d
}

function julianDayToGregorian(jdn) {
  let j = 4 * jdn + 139361631
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908
  const i = div(mod(j, 1461), 4) * 5 + 308
  const day = div(mod(i, 153), 5) + 1
  const month = mod(div(i, 153), 12) + 1
  const year = div(j, 1461) - 100100 + div(8 - month, 6)
  return { year, month, day }
}

export function toGregorian(jy, jm, jd) {
  const { gy, march } = calendar(jy)
  const jdn = gregorianToJulianDay(gy, 3, march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1
  return julianDayToGregorian(jdn)
}

export function toJalali(gy, gm, gd) {
  const jdn = gregorianToJulianDay(gy, gm, gd)
  let jy = julianDayToGregorian(jdn).year - 621
  const shape = calendar(jy)
  let k = jdn - gregorianToJulianDay(shape.gy, 3, shape.march)
  if (k >= 0) {
    if (k <= 185) return { year: jy, month: 1 + div(k, 31), day: mod(k, 31) + 1 }
    k -= 186
  } else {
    jy -= 1
    k += 179
    if (shape.leap === 1) k += 1
  }
  return { year: jy, month: 7 + div(k, 30), day: mod(k, 30) + 1 }
}

export function isLeapJalaliYear(jy) {
  return calendar(jy).leap === 0
}

/** 31 for the first six months, 30 for the next five, 29 or 30 for اسفند. */
export function jalaliMonthLength(jy, jm) {
  if (jm <= 6) return 31
  if (jm <= 11) return 30
  return isLeapJalaliYear(jy) ? 30 : 29
}

/** Whether a stepper's day/month/year is a date that exists — 31 مهر does not. */
export function isValidJalali(jy, jm, jd) {
  return jy > BREAKS[0] && jy < BREAKS[BREAKS.length - 1] && jm >= 1 && jm <= 12 && jd >= 1 && jd <= jalaliMonthLength(jy, jm)
}

/** The wire's `YYYY-MM-DD` as `{year, month, day}`, or null for anything else. */
export function parseIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim())
  if (!match) return null
  const [year, month, day] = match.slice(1).map(Number)
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { year, month, day }
}

/** `{year, month, day}` as the wire's `YYYY-MM-DD`. */
export function isoDate({ year, month, day }) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}
